import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme, commonStyles } from '../styles/theme';
import SignIn from './SignIn';
import Registration from './Registration';
import Header from './common/Header';  // Direct import

const FeatureCard = ({ title, description, icon, onClick }) => {
  const cardStyle = {
    background: `linear-gradient(to bottom right, white, ${theme.colors.primary.light})`,
    borderColor: theme.colors.neutral.light
  };

  const iconStyle = {
    background: `linear-gradient(to bottom right, ${theme.colors.primary.main}, ${theme.colors.primary.dark})`
  };

  const hoverLineStyle = {
    background: `linear-gradient(to right, ${theme.colors.accent.main}, ${theme.colors.accent.hover})`
  };

  return (
    <div onClick={onClick} className="cursor-pointer group">
      <div style={cardStyle} className={`
        relative overflow-hidden rounded-2xl p-6
        transition-all duration-300
        hover:shadow-xl hover:-translate-y-1
        ${commonStyles.card.base}
      `}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ 
            backgroundImage: `radial-gradient(circle at 2px 2px, ${theme.colors.primary.main} 1px, transparent 0)`,
            backgroundSize: '24px 24px'
          }} />
        </div>

        {/* Content */}
        <div className="relative z-10">
          <div style={iconStyle} className="w-12 h-12 mb-4 rounded-lg flex items-center justify-center text-white text-xl">
            {icon}
          </div>
          <h3 style={{ color: theme.colors.text.primary }} className="text-xl font-semibold mb-2">
            {title}
          </h3>
          <p style={{ color: theme.colors.text.secondary }} className="text-base">
            {description}
          </p>
        </div>

        {/* Hover Effect */}
        <div style={hoverLineStyle} className="absolute bottom-0 left-0 right-0 h-1 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
      </div>
    </div>
  );
};

// Modal component
const Modal = ({ children, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center">
    <div className="fixed inset-0 bg-black opacity-50" onClick={onClose}></div>
    <div className="relative z-50 w-[600px] min-w-[600px] bg-white rounded-lg shadow-xl">
      {children}
    </div>
  </div>
);

const LandingPage = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [showSignIn, setShowSignIn] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [intendedPath, setIntendedPath] = useState(null);

  const features = [
    {
      title: 'Max_Comply',
      description: 'Comprehensive quality assurance and audit management system for aviation operations.',
      icon: '🛡️',
      path: '/max-comply'
    },
    {
      title: 'SMS_Prime',
      description: 'Advanced safety management system for proactive risk assessment and mitigation.',
      icon: '⚡',
      path: '/sms-prime'
    },
    {
      title: 'RCA_Pro',
      description: 'Sophisticated root cause analysis tools for incident investigation and prevention.',
      icon: '🔍',
      path: '/rca-pro'
    },
    {
      title: 'Just_Culture_Flow',
      description: 'Foster a balanced approach to accountability and learning in aviation safety.',
      icon: '⚖️',
      path: '/just-culture'
    }
  ];

  const handleFeatureClick = (path) => {
    if (!user) {
      setIntendedPath(path);
      setShowSignIn(true);
    } else {
      navigate(path);
    }
  };

  const handleSignIn = (user) => {
    setUser(user);
    setShowSignIn(false);
    if (intendedPath) {
      navigate(intendedPath);
      setIntendedPath(null);
    }
  };

  const handleRegister = (user) => {
    setUser(user);
    setShowRegister(false);
    if (intendedPath) {
      navigate(intendedPath);
      setIntendedPath(null);
    }
  };

  const heroStyle = {
    background: `linear-gradient(135deg, ${theme.colors.primary.dark} 0%, ${theme.colors.primary.main} 100%)`
  };

  return (
    <div className="min-h-screen bg-white">
      <Header 
        user={user} 
        setUser={setUser} 
        onSignInClick={() => setShowSignIn(true)}
        onRegisterClick={() => setShowRegister(true)}
        showBackToHub={false}
      />

      {/* Hero Section */}
      <div style={heroStyle} className="relative overflow-hidden py-20 px-4">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ 
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '24px 24px',
            opacity: '0.1'
          }} />
        </div>

        {/* Content */}
        <div className="container mx-auto relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Aviation Enterprise Management System Hub
            </h1>
            <p className="text-xl text-white/80 mb-8">
              Comprehensive solutions for aviation safety, quality assurance, and operational excellence
            </p>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white via-white/20 to-transparent" />
      </div>

      {/* Features Grid */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {features.map((feature, index) => (
            <FeatureCard 
              key={index} 
              {...feature} 
              onClick={() => handleFeatureClick(feature.path)}
            />
          ))}
        </div>
      </div>

      {/* Auth Modals */}
      {showSignIn && (
        <Modal onClose={() => setShowSignIn(false)}>
          <SignIn 
            onSignIn={handleSignIn}
            onRegisterClick={() => {
              setShowSignIn(false);
              setShowRegister(true);
            }}
          />
        </Modal>
      )}

      {showRegister && (
        <Modal onClose={() => setShowRegister(false)}>
          <Registration 
            onRegister={handleRegister}
            onCancel={() => setShowRegister(false)}
          />
        </Modal>
      )}
    </div>
  );
};

export default LandingPage;
